import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { Button, Tap } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import type { McpPermission } from '@/features/integrations/integrations.types';

import {
  SECTION_TRANSITION,
  stateSwapVariants,
  SWAP_TRANSITION,
} from './motion';
import { MissingPermissionsDetails } from './server-permissions-callout';

import type { ConnectionTestState } from './types';

export function TestConnection({
  test,
  canTest,
  serverName,
  toolCount,
  errorText,
  missingPermissions = [],
  onTest,
}: {
  test: ConnectionTestState;
  canTest: boolean;
  serverName: string;
  toolCount: number;
  errorText: string;
  missingPermissions?: readonly McpPermission[];
  onTest: () => void;
}) {
  const reduce = useReducedMotion();
  const transition = reduce ? { duration: 0 } : SWAP_TRANSITION;

  let content: React.ReactNode;
  if (test === 'testing') {
    content = (
      <XStack className="items-center gap-2 rounded-lg border border-border bg-surface p-3">
        <Icons.loader className="size-4 shrink-0 animate-spin text-foreground" />
        <span className="text-sm text-foreground">Starting server…</span>
      </XStack>
    );
  } else if (test === 'success') {
    content = (
      <XStack className="items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2.5">
        <motion.span
          className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-success-3 text-success-11"
          initial={reduce ? false : { scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={reduce ? { duration: 0 } : SECTION_TRANSITION}
        >
          <Icons.check className="size-3.5" />
        </motion.span>
        <YStack className="min-w-0 flex-1">
          <span className="text-sm text-foreground">
            Connected — <b className="font-medium">{serverName}</b>
          </span>
          <span className="text-xs text-foreground-muted">
            {toolCount} {toolCount === 1 ? 'tool' : 'tools'} found · name &amp;
            description filled in below
          </span>
        </YStack>
        <Button variant="ghost" size="sm" onClick={onTest} className="shrink-0">
          Test again
        </Button>
      </XStack>
    );
  } else if (test === 'failure' && missingPermissions.length > 0) {
    content = (
      <YStack className="gap-2.5 rounded-lg border border-border bg-surface p-3">
        <XStack className="items-start gap-2.5">
          <span className="mt-px shrink-0 text-warning-11">
            <Icons.warn className="size-4" />
          </span>
          <YStack className="min-w-0 flex-1">
            <span className="text-sm font-medium text-foreground">
              Server works, but a permission is missing
            </span>
            <p className="mt-0.5 text-xs leading-relaxed text-foreground-muted">
              Grant access, then test again. Save stays disabled until a test
              passes.
            </p>
          </YStack>
          <Button
            variant="ghost"
            size="sm"
            onClick={onTest}
            className="shrink-0"
          >
            Try again
          </Button>
        </XStack>
        <div className="pl-[26px]">
          <MissingPermissionsDetails missingPermissions={missingPermissions} />
        </div>
      </YStack>
    );
  } else if (test === 'failure') {
    content = (
      <YStack className="gap-2.5 rounded-lg border border-danger-6 bg-danger-3 p-3">
        <XStack className="items-start gap-2.5">
          <span className="mt-px shrink-0 text-danger-11">
            <Icons.warnOctagon className="size-4" />
          </span>
          <YStack className="min-w-0 flex-1">
            <span className="text-sm font-medium text-foreground">
              Couldn&apos;t start the server
            </span>
            <p className="mt-0.5 text-xs leading-relaxed text-foreground-muted">
              Save stays disabled until a test passes.
            </p>
          </YStack>
          <Button
            variant="ghost"
            size="sm"
            onClick={onTest}
            className="shrink-0"
          >
            Try again
          </Button>
        </XStack>
        <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border-strong bg-background-subtle p-2.5 font-mono text-[10px] leading-relaxed text-foreground-subtle">
          {errorText}
        </pre>
      </YStack>
    );
  } else {
    content = (
      <YStack className="items-center gap-2 rounded-lg border border-dashed border-border-strongest bg-surface p-3.5">
        <Tap disabled={!canTest}>
          <Button
            variant="default"
            disabled={!canTest}
            tooltip={
              canTest ? undefined : 'Enter a package, program, or URL first'
            }
            onClick={onTest}
          >
            <Icons.play className="size-4" />
            Test connection
          </Button>
        </Tap>
        <p className="text-center text-[10px] text-foreground-muted">
          We&apos;ll start the server and read back its name, description, and
          available tools.
        </p>
      </YStack>
    );
  }

  return (
    <motion.div layout={!reduce} transition={transition}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={test}
          variants={stateSwapVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
        >
          {content}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
